import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/CTASection";
import { SERVICES } from "@/components/ServicesGrid";
import SEO from "@/components/SEO";
import { motion } from "framer-motion";

const DETAILS = {
  "modernization-assurance": {
    long:
      "A parallel verification service that creates an independent, executable specification of your legacy system's real business behavior and continuously validates modernization outcomes against it.",
    bullets: [
      "Independent Verification: evaluated separately from the modernization team.",
      "Early Detection: behavioral regressions identified instantly, not at UAT.",
      "Continuous Observability: see exactly what is changing across every stage.",
      "Non-Disruptive: works alongside your existing vendors and workflows.",
    ],
  },
  "full-modernization": {
    long:
      "End-to-end legacy modernization powered by the same verification-first approach. One accountable partner, milestone-based delivery, production-ready outcomes.",
    bullets: [
      "Milestone-based, validation-driven modernization",
      "Continuous deployment and parallel comparison",
      "Observable, auditable, production-ready",
    ],
  },
  "application-development": {
    long:
      "Full-cycle custom software engineering. Product discovery, system design, delivery. Web, mobile, backend, SaaS, delivered by senior engineers, not seat-warmers.",
    bullets: [
      "Product discovery & technical design",
      "Web, mobile, backend, and APIs",
      "Test-driven delivery with observability baked in",
      "Long-term ownership or transition to in-house teams",
    ],
  },
  cloud: {
    long:
      "Design, migrate, run and optimize your cloud footprint across AWS, Azure and GCP. Infrastructure-as-code, cost governance, security & compliance engineering.",
    bullets: [
      "Cloud migration strategy & execution",
      "Infrastructure-as-code (Terraform / Pulumi)",
      "FinOps & cost governance",
      "Security, compliance & SOC2 readiness",
    ],
  },
  consulting: {
    long:
      "Embed senior United Techlab engineers and consultants directly into your teams. Hourly, project-based, or long-term contract roles for architecture, platform, staff, and principal engineers.",
    bullets: [
      "Vetted senior consultants & tech leads",
      "Flexible engagements: hourly, project, or contract",
      "Contract-to-hire options for strategic roles",
      "On-demand architecture and technical reviews",
    ],
  },
  devops: {
    long:
      "Platform engineering and SRE, delivered as a service. We build the CI/CD, observability, and on-call muscle that high-performance engineering orgs rely on.",
    bullets: [
      "CI/CD and release engineering",
      "Kubernetes, service mesh, and platform engineering",
      "Observability, SLOs, and SRE on-call",
      "Automation that permanently removes toil",
    ],
  },
  "data-ai": {
    long:
      "Enterprise-grade data pipelines, LLM integrations, AI agents, and AI governance systems built on top of the same verification-first principles that drive our modernization work.",
    bullets: [
      "Data platforms, pipelines and warehouses",
      "LLM & RAG systems with guardrails",
      "Autonomous agents and evaluation harnesses",
      "AI governance, provenance, and compliance",
    ],
  },
};

export default function Services() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  return (
    <>
      <SEO
        title="IT Services | Modernization, Development, Cloud, AI"
        description="Seven deeply practiced service lines: modernization assurance, full modernization, application development, cloud, DevOps, IT consulting & staffing, data & AI engineering."
        path="/services"
      />
      <PageHeader
        eyebrow="> /services"
        title="Engineering partners across modernization, delivery & AI."
        subtitle="One accountable team. Seven deeply practiced service lines. Designed to plug directly into your roadmap."
      />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 space-y-4">
        {SERVICES.map((s, i) => {
          const detail = DETAILS[s.id];
          return (
            <motion.section
              key={s.id}
              id={s.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15%" }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 border border-white/10 bg-carbon p-8 lg:p-12"
              data-testid={`service-section-${s.id}`}
            >
              <div className="lg:col-span-4">
                <div className="chip text-neon-green">// 0{i + 1}</div>
                <div className={`mt-4 h-14 w-14 border inline-flex items-center justify-center ${
                  s.accent === "green"
                    ? "border-neon-green/40 text-neon-green"
                    : "border-neon-cyan/40 text-neon-cyan"
                }`}>
                  <s.icon size={22} />
                </div>
                <h2 className="mt-4 font-display font-black text-3xl md:text-4xl text-white leading-tight">
                  {s.name}
                </h2>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{s.blurb}</p>
              </div>
              <div className="lg:col-span-8 lg:border-l lg:border-white/10 lg:pl-12">
                <p className="text-base text-white/80 leading-relaxed">{detail.long}</p>
                <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {detail.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-3 text-sm text-white/70 border border-white/10 p-4 bg-obsidian"
                    >
                      <span className={`mt-1.5 h-1.5 w-1.5 ${s.accent === "green" ? "bg-neon-green" : "bg-neon-cyan"}`} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.section>
          );
        })}
      </div>

      <CTASection
        eyebrow="> /services.next_step"
        title="Want us as your engineering partner?"
        subtitle="Share your context in one message. We'll respond with a concrete, pragmatic proposal, not a brochure."
      />
    </>
  );
}
